import { ApiRoutes } from "../datasources/networking/apiRoutes";
import { TransportPerspective } from "../state/preferences";

/**
 * Backend feature ids; each comment reports the feature name in the backend catalog.
 */
export const FeatureIds = {
	// Results > Overview
	ResultsEmissions: "8c298256-36db-4c0d-ad26-fa4adcf56735", // Results, emissions
	ReportTransports: "95be4e4b-f951-417a-ba31-ca5140adafad", // Report, transports
	ReportSites: "29275b9e-c829-4bcc-a3a2-2b469fb4a31c", // Report, sites

	// Results > Transports
	ResultsTransportEmissions: "31f24884-ea4a-4a74-8e15-a3d70b827de5", // Results, transport emissions
	ResultsEmissionsByCountry: "a032af42-d9c4-45b9-8ee6-8c3197c55286", // Results, emissions by country
	ResultsEmissionsCalcMethods: "cd7bf6f7-19e0-4e24-b014-285f756c1b37", // Results, emissions, calc methods
	ResultsEmissionsCertification: "21f4c9b0-ea63-4ba9-b460-0a4df8d51dc1", // Results, emissions, certification
	ResultsGreenRouterTransports: "f93e7e41-5f7c-471e-9c82-a255886e4a0a", // Results, GR transports
	ResultsEntities: "1c419552-579d-48a4-8364-49243aff3665", // Results, entities
	ResultsThirdPartyTransports: "5c5b5a60-3696-495a-bb2a-133db8402b3c", // Results, TP transports

	// Results > Operational units
	ResultsSitesEmissions: "69fe1b31-b148-4620-87ad-841c1f36f2f1", // Results, sites emissions
	ResultsSites: "39a4c6bd-328a-450e-997b-7b4970bf0ab6", // Results, sites
	ResultsSitesConsumptions: "d3b36ca1-e1fa-472b-8581-9eaba955bcc5", // Results, sites consumptions
	ResultsThirdPartySites: "d07f21f4-18f2-40b1-909f-dce58595f4a0", // Results, TP sites

	// Data insights > Dashboard
	DashboardsSignInSuperset: "d8ce293c-b643-46c1-bc76-683586f1aa31", // Dashboards, sign-in, Superset

	// Data insights > Root cause
	RootCauseAggregate: "a08bb659-06a4-423a-a703-ebf3a6651699", // Root cause, aggregate
	RootCauseWaterfall: "4aebd30f-a953-4d56-b30e-c2eec9acc856", // Root cause, waterfall
	RootCausePerimeter: "7d4dc6d9-0810-4d09-a98f-a777c644d3b1", // Root cause, perimeter
	RootCauseTransportQuantity: "9385730a-e326-400d-9994-f7bc09e62fb9", // Root cause, transport quantity
	RootCauseModalShift: "f7f88016-b540-4e8d-9f0c-8926f2d15b20", // Root cause, modal shift
	RootCauseEmissionFactor: "e8765ae9-1f1b-4001-8528-ea1c57446615", // Root cause, emission factor

	// Master data > Operational units
	ConfigurationSites: "c014a3a6-cc3e-4717-aa78-b81d2f746e15", // Configuration, sites
	ConfigurationManageSites: "c0430c8b-b8a5-4e3e-a853-4037963ee736", // Configuration, manage sites

	// Platform settings > Admin
	AdminReadCompany: "67d2930a-2fbe-475d-b657-499ac67f571e", // Admin, read company
	AdminManageWorkspaceCompany: "2c5eb152-ddaa-4bf4-9fba-94e40a4a96ed", // Admin, manage workspace company
	AdminReadPermissions: "1539a730-de57-404f-9938-0a1a9ece06e0", // Admin, read permissions
	AdminReadRoles: "b221058b-d5ab-4ad1-9450-5626b08def2b", // Admin, read roles
	AdminManageRoles: "ffaffaad-b0dc-4bd4-b841-0305deab3bdc", // Admin, manage roles
	AdminManageUsers: "764c434d-e784-45de-b434-07a302dfdcb9", // Admin, manage users

	// Account
	CurrentUser: "d0d53f7b-71cb-4cd8-b538-eea59b5a81bd", // Current user

	// Shared by several pages (options, geocoding, user filters)
	UtilsReadOptions: "a74a4a04-3b8a-4655-bee7-9ae2f6b0ef98", // Utils, read options
	ServicesGeocoding: "ce71f00f-8261-43c6-81dc-c058a936093d", // Services, geocoding
	CurrentUserFiltersAvailable: "19479441-2cb1-4e1e-86e7-13d1f830aba4", // Current user, filters, available
	CurrentUserFiltersCustom: "a187d541-6dbe-4998-bb26-d39d90df1a84", // Current user, filters, custom
	CurrentUserFiltersFavorite: "6fbc66ce-79c0-4403-8082-3760bafc3907", // Current user, filters, favorite
};

/**
 * FeatureKey is the name of a backend feature.
 */
export type FeatureKey = keyof typeof FeatureIds;

/**
 * Backend variant ids of the features that depend on the transports perspective.
 */
export const FeatureVariantIds: Partial<Record<FeatureKey, Record<TransportPerspective, string>>> = {
	ResultsEmissions: {
		logical: "479b6a05-a706-49e8-8f01-50825daff64e",
		operational: "5d25ca75-4954-48ad-89ef-b5e292ceaa03",
	},
	ReportTransports: {
		logical: "589a2a65-fd1e-4463-84a3-b04581cb2cb5",
		operational: "75bbd3e3-20d4-4865-b850-6db37daa4862",
	},
	ResultsTransportEmissions: {
		logical: "d1639215-be2c-4320-a387-de8850b8dd2c",
		operational: "36bf2601-b311-45a7-96ed-1ab1a409aa01",
	},
	RootCauseAggregate: {
		logical: "b62e5fef-4b79-4ae3-8a78-f22b8746040f",
		operational: "94c8e7d4-775e-4c30-a05a-0ec2857d11ca",
	},
	RootCauseWaterfall: {
		logical: "7e5119ba-2a24-4d58-b2e6-6451fb2a2bcd",
		operational: "e918e5d4-0c1b-403f-b82c-4883f788493a",
	},
	RootCausePerimeter: {
		logical: "eb3b1e6e-9537-40a1-812c-3f4525f97d22",
		operational: "2edfd69b-d561-4a7c-9990-70ebe5a99a01",
	},
	RootCauseTransportQuantity: {
		logical: "dad3d2a3-8590-4aaf-a01e-fa739eb32990",
		operational: "80c96519-081d-4db7-b03c-fe127557a8b4",
	},
	RootCauseModalShift: {
		logical: "2288fb5b-6bc4-44bb-91c6-67c21312f310",
		operational: "604dbb4a-76a4-43ea-942c-9550e62b7a1b",
	},
	RootCauseEmissionFactor: {
		logical: "57d57afe-3c81-4a02-a454-d69050015a81",
		operational: "3825e464-0d07-496e-9de4-b8265e7cba80",
	},
};

export type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

/**
 * ApiRoutePermission maps each HTTP method used on a route to the backend feature it requires;
 * null means that the call requires no feature (public routes, or routes not in the features catalog yet).
 */
export type ApiRoutePermission = Partial<Record<HttpMethod, FeatureKey | null>>;

/**
 * ApiPermissions maps every API route called by the UI to the backend features it requires, mirroring
 * the backend features catalog; it is the single source of truth for permission checks.
 */
export const ApiPermissions: Record<ApiRoutes, ApiRoutePermission> = {
	// Results > Overview
	[ApiRoutes.ResultsEmissions]: { POST: "ResultsEmissions" },
	[ApiRoutes.ResultsEmissionsIntervals]: { POST: "ResultsEmissions" },
	[ApiRoutes.ResultsScopeEmissions]: { POST: "ResultsEmissions" },
	[ApiRoutes.ResultsActivityEmissions]: { POST: "ResultsEmissions" },
	[ApiRoutes.ResultsTranposrtPDFDownload]: { POST: "ReportTransports" },
	[ApiRoutes.OperationalUnitsPDFDownload]: { POST: "ReportSites" },

	// Results > Transports
	[ApiRoutes.ResultsTransportEmission]: { POST: "ResultsTransportEmissions" },
	[ApiRoutes.ResultsEmissionType]: { POST: "ResultsTransportEmissions" },
	[ApiRoutes.ResultsChainTransportEmission]: { POST: "ResultsTransportEmissions" },
	[ApiRoutes.ResultThirdPartyEnvironmentalKpi]: { POST: "ResultsEmissions" },
	[ApiRoutes.ResultsCountryEmission]: { POST: "ResultsEmissions" },
	[ApiRoutes.ResultThirdPartyCalculationMethods]: { POST: "ResultsEmissionsCalcMethods" },
	[ApiRoutes.ResultThirdPartyNodesCertification]: { POST: "ResultsEmissionsCertification" },

	// Results > Transports > GreenRouter
	[ApiRoutes.ResultsTransportGreenrouter]: { POST: "ResultsGreenRouterTransports" },
	[ApiRoutes.ResultsEntitiesGreenrouter]: { POST: "ResultsEntities" },
	[ApiRoutes.ResultsTripDetails]: { GET: "ResultsGreenRouterTransports" },
	[ApiRoutes.ResultsTransportEntitiesGreenRouter]: { POST: "ResultsEntities" },
	[ApiRoutes.ResultsEntityDetails]: { GET: "ResultsEntities" },

	// Results > Transports > Third party
	[ApiRoutes.ResultsTripsThirdParty]: { POST: "ResultsThirdPartyTransports" },
	[ApiRoutes.ResultsTransportThirdPartyDetail]: { GET: "ResultsThirdPartyTransports" },

	// Results > Operational units
	[ApiRoutes.ResultsSitesEmissions]: { POST: "ResultsSitesEmissions" },
	[ApiRoutes.ResultsEmissionsSitesSource]: { POST: "ResultsSitesEmissions" },
	[ApiRoutes.ResultGreerouterNodesMap]: { POST: "ResultsSites" },
	[ApiRoutes.ResultsConsumptionIntervals]: { POST: "ResultsSitesConsumptions" },
	[ApiRoutes.ResultsElectricityConsumption]: { POST: "ResultsSitesConsumptions" },
	[ApiRoutes.ResultsWaterConsumption]: { POST: "ResultsSitesConsumptions" },
	[ApiRoutes.ResultsFuelConsumption]: { POST: "ResultsSitesConsumptions" },
	[ApiRoutes.ResultsRefrigerantsConsumption]: { POST: "ResultsSitesConsumptions" },
	[ApiRoutes.ResultsHeatSteamConsumption]: { POST: "ResultsSitesConsumptions" },
	[ApiRoutes.ResultsProcessGasConsumption]: { POST: "ResultsSitesConsumptions" },

	// Results > Operational units > GreenRouter
	[ApiRoutes.ResultGreerouterSitesList]: { POST: "ResultsSites" },
	[ApiRoutes.ResultsSitesNodeDetailsConsumption]: { POST: "ResultsSitesConsumptions" },
	[ApiRoutes.ResultsSitesNodeDetails]: { GET: "ResultsSites" },
	[ApiRoutes.ResultsNodeElectricityConsumption]: { POST: "ResultsSitesConsumptions" },
	[ApiRoutes.ResultsNodeFuelConsumption]: { POST: "ResultsSitesConsumptions" },
	[ApiRoutes.ResultsNodeRefrigerantsConsumption]: { POST: "ResultsSitesConsumptions" },
	[ApiRoutes.ResultsNodeProcessGasConsumption]: { POST: "ResultsSitesConsumptions" },
	[ApiRoutes.ResultsNodeHeatSteamConsumption]: { POST: "ResultsSitesConsumptions" },
	[ApiRoutes.ResultsNodeWaterConsumption]: { POST: "ResultsSitesConsumptions" },

	// Results > Operational units > Third party
	[ApiRoutes.ResultsSitesThirdParty]: { POST: "ResultsThirdPartySites" },
	[ApiRoutes.ResultsSitesThirdPartyDetail]: { GET: "ResultsThirdPartySites" },

	// Data insights > Dashboard
	// TODO: not in the features catalog yet.
	[ApiRoutes.SupersetDashboards]: { GET: null },

	// Data insights > Root cause
	[ApiRoutes.RootCauseWidjetAggregate]: { POST: "RootCauseAggregate" },
	[ApiRoutes.RootCauseWidjetWaterfall]: { POST: "RootCauseWaterfall" },
	[ApiRoutes.RootCauseWidjetPerimeter]: { POST: "RootCausePerimeter" },
	[ApiRoutes.RootCauseWidjetTransportQuantity]: { POST: "RootCauseTransportQuantity" },
	[ApiRoutes.RootCauseWidjetModalShift]: { POST: "RootCauseModalShift" },
	[ApiRoutes.RootCauseWidjetEmissionsChangeTotal]: { POST: "RootCauseEmissionFactor" },
	[ApiRoutes.RootCauseWidjetEmissionsChangeRoad]: { POST: "RootCauseEmissionFactor" },
	[ApiRoutes.RootCauseWidjetEmissionsChangeTrain]: { POST: "RootCauseEmissionFactor" },
	[ApiRoutes.RootCauseWidjetEmissionsChangeShip]: { POST: "RootCauseEmissionFactor" },
	[ApiRoutes.RootCauseWidjetEmissionsChangeAir]: { POST: "RootCauseEmissionFactor" },
	[ApiRoutes.RootCauseWidjetEmissionsChangeWaterways]: { POST: "RootCauseEmissionFactor" },

	// What if > CarbonSnap
	// TODO: CarbonSnap routes are not in the features catalog yet.
	[ApiRoutes.CarbonSnapNodesSearch]: { GET: null },
	[ApiRoutes.CarbonSnapGeocode]: { POST: null },
	[ApiRoutes.CarbonSnapCalculate]: { POST: null },

	// Master data > Operational units
	[ApiRoutes.ConfigurationDeleteNodes]: { PUT: "ConfigurationManageSites" },
	[ApiRoutes.ConfigurationSitesNodes]: { POST: "ConfigurationManageSites" },
	[ApiRoutes.ConfigurationSearchNodes]: { POST: "ConfigurationSites" },
	[ApiRoutes.ConfigurationSitesNodesUpdate]: { PUT: "ConfigurationSites" },
	[ApiRoutes.ConfigurationFilters]: { GET: "ConfigurationSites", PUT: "ConfigurationManageSites" },
	[ApiRoutes.ConfigurationGoodFlows]: { GET: "ConfigurationSites", PUT: "ConfigurationManageSites" },

	// Platform settings > Admin
	[ApiRoutes.AdminCompanies]: { GET: "AdminReadCompany", PUT: "AdminManageWorkspaceCompany" },
	[ApiRoutes.AdminPermissions]: { GET: "AdminReadPermissions" },
	// TODO: POST is not in the features catalog yet.
	[ApiRoutes.AdminRoles]: { GET: "AdminReadRoles", POST: null, PUT: "AdminManageRoles", DELETE: "AdminManageRoles" },
	// TODO: GET is exposed by the catalog as administration/workspaces/current/users.
	[ApiRoutes.AdminUsers]: { GET: null, POST: "AdminManageUsers", PUT: "AdminManageUsers" },
	// TODO: exposed by the catalog as administration/workspaces/current/users/{id}/filters.
	[ApiRoutes.AdminUserFilters]: { GET: null, PUT: null },

	// Account
	[ApiRoutes.AuthMe]: { GET: "CurrentUser", PUT: "CurrentUser" },
	[ApiRoutes.AuthMePassword]: { POST: "CurrentUser" },

	// Shared by several pages (options, geocoding, user filters)
	[ApiRoutes.UtilsOptions]: { GET: "UtilsReadOptions" },
	[ApiRoutes.ServicesGeocodeAddress]: { POST: "ServicesGeocoding" },
	[ApiRoutes.ServicesReverseGeocodeLocation]: { POST: "ServicesGeocoding" },
	[ApiRoutes.FiltersAvailable]: { GET: "CurrentUserFiltersAvailable" },
	[ApiRoutes.CustomFilter]: { GET: "CurrentUserFiltersCustom", POST: "CurrentUserFiltersCustom" },
	[ApiRoutes.CustomMenuFilters]: { PUT: "CurrentUserFiltersCustom", DELETE: "CurrentUserFiltersCustom" },
	[ApiRoutes.PinMenuFilter]: { PUT: "CurrentUserFiltersFavorite", DELETE: "CurrentUserFiltersFavorite" },

	// Authentication (public)
	[ApiRoutes.AuthSignIn]: { POST: null },
	[ApiRoutes.AuthSignInSelectWorkspace]: { POST: null },
	[ApiRoutes.AuthWorkspaces]: { GET: null },
	[ApiRoutes.AuthRecoverPasswordInitiate]: { POST: null },
	[ApiRoutes.AuthRecoverPasswordComplete]: { POST: null },
};
